/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTheme } from '@material-ui/core'
import { useArticleIdFilter } from 'features/BillOfMaterials/BoMItemFilter/Components/ArticleIdFilter/useArticleIdFilter'
import { useWorkingStepFilter } from 'features/BillOfMaterials/BoMItemFilter/Components/WorkingStepsFilter/useWorkingStepFilter'
import { RemoveFiltersIcon } from 'features/BillOfMaterials/ProjectHeader/RemoveFiltersIcon'
import { MoneyAxisTick } from 'features/BomItemDetailsDrawer/Graph/YAxisTick'
import { isEqual } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import { useAppDispatch } from 'store/configureStore'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { CategoricalChartState } from 'recharts/types/chart/generateCategoricalChart'
import { RootAppState } from 'store/configureStore'
import { getPriceSummaryKey } from 'store/Project/ProjectStateUtils'
import { projectConstants } from 'store/Project/ProjectStore'
import { PriceSummaryDto } from '../../../services/APIs/InternalAPI/internal-api.contracts'
import { CostFactorLabel } from './CostFactorLabel'
import './CostPriceGraph.scss'
import { MarginEditor } from './MarginEditor'
import { MarginGraphTooltip } from './MarginGraphTooltip'

const CostPriceGraph = () => {
  const { t } = useTranslation()

  const data = useAppSelector(
    (state: RootAppState) =>
      Object.values(state.project.priceSummaries)
        .map((x) => ({
          ...x,
          discountValue: {
            ...x.discountValue,
            value: x.discountValue.value * -1,
          },
        }))
        .sort((a, b) => b.totalSalesPrice.value - a.totalSalesPrice.value),
    isEqual
  )

  const [selectedData, setSelectedData] = useState<PriceSummaryDto>(undefined)

  const currentTheme = useTheme()

  const isLocked = useAppSelector(
    (state: RootAppState) => state.project.activeProject.isLocked
  )

  const dispatch = useAppDispatch()

  const setData = (priceSummary: PriceSummaryDto) => {
    dispatch({
      type: projectConstants.SET_MARGIN_DATA,
      priceSummary,
    })
  }

  useEffect(() => {
    if (selectedData) {
      setSelectedData(
        data[
          data.findIndex(
            (x) => getPriceSummaryKey(x) === getPriceSummaryKey(selectedData)
          )
        ]
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const { handleChangeFilter: handleChangeMaterialFilter } =
    useArticleIdFilter()
  const { handleChangeFilter: handleChangeWSFilter } = useWorkingStepFilter()

  const handleSetSelectedData = (nextState: CategoricalChartState) => {
    if (!nextState) return

    const priceSummary = data[nextState?.activeTooltipIndex]

    if (!priceSummary) return

    setSelectedData(priceSummary)

    setTimeout(() => {
      if (priceSummary?.isWorkingStep) {
        handleChangeWSFilter(priceSummary.workingStep.primaryWorkingStep, true)
      } else if (priceSummary.isMaterial) {
        handleChangeMaterialFilter(priceSummary.materialId)
      }
    }, 300)
  }

  return (
    <div className="cost-summary">
      <div className="cost-graph--container">
        <div style={{ position: 'absolute', top: 0, left: '1em', zIndex: 1 }}>
          <RemoveFiltersIcon />
        </div>
        <div
          className="graph"
          style={{
            height: `${data.length * 70}px`,
            minHeight: '220px',
            position: 'relative',
          }}
        >
          <ResponsiveContainer height={'100%'}>
            <BarChart
              data={data}
              layout="vertical"
              barCategoryGap={16}
              barGap={8}
              barSize={32}
              margin={{
                left: 16,
                right: 16,
              }}
              onClick={handleSetSelectedData}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <Tooltip
                content={(props) => <MarginGraphTooltip {...props} />}
                cursor={true}
                contentStyle={{
                  zIndex: 10,
                  backgroundColor: 'white',
                }}
              />
              <Bar
                dataKey="totalCostPrice.value"
                name="total cost price"
                stackId="a"
                className="barInfo"
                fill={currentTheme.palette.info.main}
                onClick={(e) => {
                  setSelectedData(e)
                }}
                height={16}
              >
                <Cell
                  cursor="pointer"
                  overflow={'scroll'}
                  // onClick={(e) => setSelectedData(e)}
                />
                {/* <LabelList dataKey="name" position="left" /> */}
              </Bar>
              <Bar
                dataKey="surchargeValue.value"
                name={t('common:surcharge')}
                stackId="a"
                fill={currentTheme.palette.success.main}
                onClick={(e) => {
                  setSelectedData(e)
                }}
              />
              <Bar
                dataKey="discountValue.value"
                name={t('common:discount')}
                stackId="a"
                fill={currentTheme.palette.secondary.main}
                onClick={(e: PriceSummaryDto) => {
                  setSelectedData(e)
                }}
              >
                {data.map((entry, index) => {
                  if (!entry) return null
                  return (
                    <Cell
                      cursor="pointer"
                      strokeWidth={
                        selectedData &&
                        entry?.description === selectedData?.description
                          ? 1
                          : 0
                      }
                      stroke="black"
                      fill={currentTheme.palette.secondary.main}
                      key={`cell-${index}`}
                    />
                  )
                })}
              </Bar>
              <XAxis
                type="number"
                orientation="top"
                ticks={data.map((x) => x.totalSalesPrice.value)}
                tick={<MoneyAxisTick defaultMoney={data[0].totalCostPrice} />}
                minTickGap={0}
                interval={'preserveStartEnd'}
                allowDataOverflow={true}
                domain={[0, 'dataMax']}
              />
              <YAxis
                dataKey="description"
                type="category"
                axisLine={true}
                stroke={'2px solid black'}
                tick={(props) => (
                  <CostFactorLabel
                    {...props}
                    priceSummaries={data}
                    selectedData={selectedData}
                  />
                )}
                width={240}
                tickMargin={32}
                minTickGap={0}
              />

              <Legend verticalAlign="top" layout="horizontal" align="center" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="margin-editor-container">
          <MarginEditor
            selectedData={selectedData}
            setData={setData}
            setSelectedData={setSelectedData}
            disabled={isLocked}
          />
        </div>
      </div>
    </div>
  )
}

export { CostPriceGraph }
